'use client';

import {Draft} from 'immer';
import {
    FC,
    PropsWithChildren,
    Ref,
    createContext,
    forwardRef,
    useContext,
    useEffect,
    useRef,
} from 'react';
import {StoreApi, useStore} from 'zustand';

type UnwrapImmerDraft<T> = T extends Draft<infer U> ? U : T;

export const createProvidedStore = <State, Props>(
    createStore: (props: Props) => StoreApi<State>,
) => {
    const ZustandContext = createContext<StoreApi<State> | null>(null);

    const StoreProvider: FC<Props & PropsWithChildren> = ({
        children,
        ...props
    }) => {
        const storeRef = useRef<StoreApi<State>>(undefined);
        if (!storeRef.current) {
            storeRef.current = createStore(props as Props);
        }

        const prevProps = useRef(props);

        useEffect(() => {
            const newState = {...storeRef.current?.getState()};
            // shallow compare each prop
            Object.keys(props).forEach(key => {
                // @ts-ignore
                if (props[key] !== prevProps.current[key]) {
                    // @ts-ignore
                    newState[key] = props[key];
                }
            });
            prevProps.current = props;
            storeRef.current?.setState(newState);
        }, [...Object.values(props)]);

        return (
            <ZustandContext.Provider value={storeRef.current}>
                {children}
            </ZustandContext.Provider>
        );
    };

    const useProvidedStore: <T>(
        selector: (state: State) => T,
    ) => UnwrapImmerDraft<T> = <T,>(selector: (state: State) => T) => {
        const store = useContext(ZustandContext);

        if (!store) {
            throw new Error('Store is missing the provider');
        }

        return useStore(store, selector) as UnwrapImmerDraft<T>;
    };

    const withStoreProvider = <P extends PropsWithChildren>(
        WrappedComponent: FC<P>,
        storeProps?: Props,
    ) => {
        function ComponentWithPlayerContext(props: P, ref: Ref<unknown>) {
            const context = useContext(ZustandContext);
            if (context) {
                return <WrappedComponent ref={ref} {...props} />;
            }
            return (
                <StoreProvider {...(storeProps as Props)}>
                    <WrappedComponent ref={ref} {...props} />
                </StoreProvider>
            );
        }

        return forwardRef(ComponentWithPlayerContext);
    };

    return {
        useStore: useProvidedStore,
        StoreProvider: StoreProvider,
        withStoreProvider,
    };
};
